import { ENABLED_INTEGRATIONS_QUERY_KEY } from "@frontend-shared/constants";
import type {
  EIntegrationProvider,
  IEnabledIntegrationProviderInfo,
  IEnabledIntegrationsResponse,
} from "@frontend-shared/types";
import {
  filterEnabledIntegrations,
  readEnabledIntegrationOverrides,
} from "@frontend-shared/utils";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useContext, useMemo } from "react";

import { integrationApi } from "@/api/services";
import { AuthContext } from "@/contexts/AuthContext";
import { localStorageAdapter } from "@/lib/storage-adapter";

// NOTE: NSAI-1249 — the reading and the filtering both moved to
// @frontend-shared/utils/feature-flag-integrations so the mobile app applies
// the same rule instead of hiding every ENABLED_INTEGRATIONS_FF provider
// outright. Kept exported under its original name: several web callers import
// it, and the storage key and CSV format are unchanged.
export const getEnabledIntegrationsFromStorage = (): EIntegrationProvider[] =>
  readEnabledIntegrationOverrides(localStorageAdapter);

// NOTE: 5 minutes staleTime to prevent excessive refetches across components
const ENABLED_INTEGRATIONS_STALE_TIME = 5 * 60 * 1000;

// NOTE: `rest` is the react-query result minus its `data`, which this hook
// replaces with the filtered provider list. Spelled out rather than inferred —
// the repo requires an explicit return type, and this hook has many callers.
interface IUseGetEnabledIntegrationsResult extends Omit<
  UseQueryResult<IEnabledIntegrationsResponse>,
  "data"
> {
  data: EIntegrationProvider[];
  enabledProviderInfo: Map<
    EIntegrationProvider,
    IEnabledIntegrationProviderInfo
  >;
}

export const useGetEnabledIntegrations = (
  enabled: boolean = true,
): IUseGetEnabledIntegrationsResult => {
  // NOTE: Read permissions directly from context to prevent 403 spam
  // when user lacks integration:read. Inside AuthProvider the context is
  // undefined — permission gating there is handled via the `enabled` param.
  const authContext = useContext(AuthContext);
  const hasIntegrationReadPermission = authContext
    ? (authContext.projectPermissions?.integration?.includes("read") ?? false)
    : true;

  const { data, ...rest } = useQuery({
    queryKey: [ENABLED_INTEGRATIONS_QUERY_KEY],
    queryFn: () => integrationApi.getEnabledIntegrations(),
    enabled: enabled && hasIntegrationReadPermission,
    staleTime: ENABLED_INTEGRATIONS_STALE_TIME,
  });

  // NOTE: Filter enabledFF integrations based on localStorage
  // NOTE: localStorage is read on component mount (page load)
  const enabledProviderInfo = useMemo<IEnabledIntegrationProviderInfo[]>(() => {
    if (!data) return [];

    const enabledFromStorage = getEnabledIntegrationsFromStorage();
    return filterEnabledIntegrations({ response: data, enabledFromStorage });
  }, [data]);

  // NOTE: Flat list of provider keys (existing consumers use this for ID
  // membership checks; the rich `enabledProviderInfo` carries the new UI hints).
  const enabledIntegrations = useMemo<EIntegrationProvider[]>(
    () => enabledProviderInfo.map((info) => info.provider),
    [enabledProviderInfo],
  );

  // NOTE: Map keyed by provider for O(1) lookups in the connect flow
  // (showConnectorPicker / requiresConfigModal hints). Memoised rather than
  // built inline: an inline `new Map(...)` is a fresh reference on every render,
  // which defeats the memoisation of every consumer that takes it as a
  // dependency — the array behind it is already stable.
  const enabledProviderInfoMap = useMemo(
    () => new Map(enabledProviderInfo.map((info) => [info.provider, info])),
    [enabledProviderInfo],
  );

  return {
    ...rest,
    data: enabledIntegrations,
    enabledProviderInfo: enabledProviderInfoMap,
  };
};
